import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Create_Order, Verify_Payment } from '../../services/oprations/Payment';
import { useTheme } from '../../Context/themeContext';

// ─── Mock API hooks (replace with real API calls) ───────────────────────────
const useOrgDetails = () => {
  const { user } = useSelector((state) => state.profile);
  const userDetails = typeof user === 'string' ? JSON.parse(user) : user;
  return userDetails;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color = 'blue', icon }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const colors = {
    blue: { 
      bg: isDark ? 'bg-blue-900/20' : 'bg-blue-100/50', 
      border: isDark ? 'border-blue-700/30' : 'border-blue-300', 
      text: isDark ? 'text-blue-300' : 'text-blue-700', 
      val: isDark ? 'text-white' : 'text-gray-900' 
    },
    green: { 
      bg: isDark ? 'bg-green-900/20' : 'bg-green-100/50', 
      border: isDark ? 'border-green-700/30' : 'border-green-300', 
      text: isDark ? 'text-green-300' : 'text-green-700', 
      val: isDark ? 'text-green-100' : 'text-gray-900' 
    },
    amber: { 
      bg: isDark ? 'bg-amber-900/20' : 'bg-amber-100/50', 
      border: isDark ? 'border-amber-700/30' : 'border-amber-300', 
      text: isDark ? 'text-amber-300' : 'text-amber-700', 
      val: isDark ? 'text-amber-100' : 'text-gray-900' 
    },
    red: { 
      bg: isDark ? 'bg-red-900/20' : 'bg-red-100/50', 
      border: isDark ? 'border-red-700/30' : 'border-red-300', 
      text: isDark ? 'text-red-300' : 'text-red-700', 
      val: isDark ? 'text-red-100' : 'text-gray-900' 
    },
  };
  const c = colors[color];
  return (
    <div className={`rounded-xl border ${c.bg} ${c.border} p-5 flex flex-col gap-2 transition-colors duration-300`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-medium uppercase tracking-wider ${c.text}`}>{label}</p>
        {icon && <span className={`text-lg ${c.text}`}>{icon}</span>}
      </div>
      <p className={`text-2xl font-bold ${c.val}`}>{value}</p>
      {sub && <p className={`text-xs ${c.text} opacity-70`}>{sub}</p>}
    </div>
  );
};

const UsageBar = ({ label, used, total, unit = '' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const pct = Math.min(Math.round((used / total) * 100), 100);
  const barColor = pct > 80 ? 'bg-red-500' : pct > 60 ? 'bg-amber-500' : 'bg-blue-500';
  const textColor = isDark ? 'text-blue-300/70' : 'text-gray-600';
  const textWhite = isDark ? 'text-white' : 'text-gray-900';
  const textBlue = isDark ? 'text-blue-400/50' : 'text-gray-400';
  const bgBar = isDark ? 'bg-blue-900/40' : 'bg-gray-200';
  
  return (
    <div className="mb-4">
      <div className="flex justify-between mb-1">
        <span className={`text-xs ${textColor}`}>{label}</span>
        <span className={`text-xs font-medium ${textWhite}`}>{used}{unit} / {total}{unit} <span className={textBlue}>({pct}%)</span></span>
      </div>
      <div className={`h-1.5 ${bgBar} rounded-full overflow-hidden`}>
        <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Badge = ({ status }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const map = {
    active:    isDark ? 'bg-green-900/40 text-green-300 border-green-700/40' : 'bg-green-100 text-green-700 border-green-300',
    inactive:  isDark ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-gray-200 text-gray-600 border-gray-300',
    overdue:   isDark ? 'bg-red-900/40 text-red-300 border-red-700/40' : 'bg-red-100 text-red-700 border-red-300',
    pending:   isDark ? 'bg-amber-900/40 text-amber-300 border-amber-700/40' : 'bg-amber-100 text-amber-700 border-amber-300',
    completed: isDark ? 'bg-green-900/40 text-green-300 border-green-700/40' : 'bg-green-100 text-green-700 border-green-300',
    refunded:  isDark ? 'bg-purple-900/40 text-purple-300 border-purple-700/40' : 'bg-purple-100 text-purple-700 border-purple-300',
    failed:    isDark ? 'bg-red-900/40 text-red-300 border-red-700/40' : 'bg-red-100 text-red-700 border-red-300',
    pro:       isDark ? 'bg-blue-900/40 text-blue-300 border-blue-700/40' : 'bg-blue-100 text-blue-700 border-blue-300',
    basic:     isDark ? 'bg-amber-900/30 text-amber-300 border-amber-700/30' : 'bg-amber-100 text-amber-700 border-amber-300',
    enterprise:isDark ? 'bg-purple-900/30 text-purple-300 border-purple-700/30' : 'bg-purple-100 text-purple-700 border-purple-300',
    paid:      isDark ? 'bg-green-900/40 text-green-300 border-green-700/40' : 'bg-green-100 text-green-700 border-green-300',
    unpaid:    isDark ? 'bg-red-900/40 text-red-300 border-red-700/40' : 'bg-red-100 text-red-700 border-red-300',
  };
  const cls = map[status?.toLowerCase()] || map.inactive;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}>
      {status}
    </span>
  );
};

// ─── Manager Access Modal ────────────────────────────────────────────────────
const ManagerAccessModal = ({ managers, onClose, onSave }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [selected, setSelected] = useState(
    managers.filter(m => m.hasBillingAccess).map(m => m.id)
  );
  const toggle = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const bgModal = isDark ? 'bg-[#0d1526]' : 'bg-white';
  const borderColor = isDark ? 'border-blue-800/50' : 'border-gray-300';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-blue-400/60' : 'text-gray-600';
  //const textMuted = isDark ? 'text-blue-400/50' : 'text-gray-500';
  const bgHover = isDark ? 'hover:bg-blue-900/20' : 'hover:bg-gray-100';
  const bgItem = isDark ? 'bg-blue-900/10' : 'bg-gray-50';
  const borderItem = isDark ? 'border-blue-800/30' : 'border-gray-200';
  const bgCheck = isDark ? 'bg-green-900/30 text-green-300 border-green-700/30' : 'bg-green-100 text-green-700 border-green-300';
  const bgUncheck = isDark ? 'bg-gray-800 text-gray-500 border-gray-700' : 'bg-gray-200 text-gray-500 border-gray-200';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDark ? 'bg-black/70' : 'bg-gray-500/30'} backdrop-blur-sm`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md ${bgModal} border ${borderColor} rounded-2xl shadow-2xl p-6 transition-colors duration-300`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className={`text-lg font-bold ${textPrimary}`}>Billing Access — Managers</h3>
          <button onClick={onClose} className={`text-xl leading-none ${isDark ? 'text-blue-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>✕</button>
        </div>
        <p className={`mb-4 text-xs ${textSecondary}`}>
          Select which managers can view the Billing &amp; Plan section.
        </p>
        <div className="pr-1 space-y-2 overflow-y-auto max-h-64">
          {managers.map(m => (
            <label
              key={m.id}
              className={`flex items-center gap-3 p-3 transition-colors border cursor-pointer rounded-xl ${borderItem} ${bgItem} ${bgHover}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(m.id)}
                onChange={() => toggle(m.id)}
                className="w-4 h-4 accent-blue-500"
              />
              <div className="flex items-center flex-1 gap-3">
                <div className={`flex items-center justify-center w-8 h-8 text-xs font-bold rounded-full ${isDark ? 'text-blue-200 bg-blue-700/40' : 'text-blue-700 bg-blue-200'}`}>
                  {m.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className={`text-sm font-medium ${textPrimary}`}>{m.name}</p>
                  <p className={`text-xs ${isDark ? 'text-blue-400/60' : 'text-gray-500'}`}>{m.email}</p>
                </div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${selected.includes(m.id) ? bgCheck : bgUncheck}`}>
                {selected.includes(m.id) ? 'Access' : 'No Access'}
              </span>
            </label>
          ))}
        </div>
        <div className="flex gap-3 mt-5">
          <button
            onClick={() => { onSave(selected); onClose(); }}
            className="flex-1 py-3 text-sm font-semibold text-white transition-colors bg-blue-700 hover:bg-blue-600 rounded-xl"
          >
            Save Changes
          </button>
          <button
            onClick={onClose}
            className={`flex-1 py-3 text-sm transition-colors border rounded-xl ${isDark ? 'text-blue-300 border-blue-800/50 hover:text-white' : 'text-gray-600 border-gray-300 hover:text-gray-900'}`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Recharge Modal ──────────────────────────────────────────────────────────
const RechargeModal = ({ onClose, onPay }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [amount, setAmount] = useState('');
  const quickAmounts = [500, 1000, 2000, 5000];
  
  const bgModal = isDark ? 'bg-[#0d1526]' : 'bg-white';
  const borderColor = isDark ? 'border-blue-800/50' : 'border-gray-300';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-blue-400/50' : 'text-gray-500';
  const bgInput = isDark ? 'bg-black/50' : 'bg-gray-50';
  const borderInput = isDark ? 'border-blue-800/60' : 'border-gray-300';
  
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDark ? 'bg-black/70' : 'bg-gray-500/30'} backdrop-blur-sm`}
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md ${bgModal} border ${borderColor} rounded-2xl shadow-2xl p-6 transition-colors duration-300`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className={`flex items-center gap-2 text-lg font-bold ${textPrimary}`}>
            <span className="text-blue-400">💳</span> Add Funds
          </h3>
          <button onClick={onClose} className={`text-xl leading-none ${isDark ? 'text-blue-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}>✕</button>
        </div>
        <div className="grid grid-cols-4 gap-2 mb-4">
          {quickAmounts.map(a => (
            <button
              key={a}
              onClick={() => setAmount(String(a))}
              className={`py-2 rounded-lg text-sm font-medium border transition-all ${
                amount === String(a)
                  ? 'bg-blue-700 border-blue-500 text-white'
                  : `${isDark ? 'border-blue-800/40 text-blue-300 hover:bg-blue-900/30' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`
              }`}
            >
              ₹{a}
            </button>
          ))}
        </div>
        <div className="relative mb-4">
          <span className={`absolute text-lg font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'} -translate-y-1/2 left-4 top-1/2`}>₹</span>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="Enter custom amount"
            className={`w-full ${bgInput} border ${borderInput} rounded-xl pl-8 pr-4 py-3.5 ${textPrimary} placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm transition-colors duration-300`}
            autoFocus
          />
        </div>
        <p className={`mb-4 text-xs ${textMuted}`}>Min recharge: ₹100 • Powered by Razorpay</p>
        <button
          onClick={() => { if (amount && parseFloat(amount) >= 100) onPay(amount); }}
          className="w-full py-3.5 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-600 hover:to-blue-800 text-white font-bold rounded-xl transition-all text-sm border border-blue-500/20"
        >
          Pay ₹{amount || '0'} via Razorpay
        </button>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const OrgBillingDashboard = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const dispatch = useDispatch();
  const org = useOrgDetails();

  const [loading, setLoading] = useState(true);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showManagerModal, setShowManagerModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Theme-aware classes
  const bgPrimary = isDark ? 'bg-[#0B0F1C]' : 'bg-gray-50';
  //const bgCard = isDark ? 'bg-blue-950/60' : 'bg-white/80';
  const bgTabContainer = isDark ? 'bg-blue-950/30' : 'bg-gray-100/50';
  const bgTabActive = isDark ? 'bg-blue-700' : 'bg-blue-500';
  const bgTabInactive = isDark ? 'text-blue-400/60 hover:text-blue-300' : 'text-gray-500 hover:text-gray-700';
  const bgSection = isDark ? 'bg-blue-950/10' : 'bg-gray-50/50';
  const bgItem = isDark ? 'bg-black/30' : 'bg-white';
  const bgTableHover = isDark ? 'hover:bg-blue-900/10' : 'hover:bg-gray-50';
  
  const borderColor = isDark ? 'border-blue-800/30' : 'border-gray-200';
  //const borderLight = isDark ? 'border-blue-800/20' : 'border-gray-200';
  const borderItemColor = isDark ? 'border-blue-800/20' : 'border-gray-200';
  
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textSecondary = isDark ? 'text-blue-300/60' : 'text-gray-600';
  //const textMuted = isDark ? 'text-blue-400/50' : 'text-gray-500';
  //const textLight = isDark ? 'text-blue-400/60' : 'text-gray-500';
  //const textBlue = isDark ? 'text-blue-300' : 'text-blue-700';
  //const textBlueLight = isDark ? 'text-blue-400' : 'text-blue-600';
  
  const shadowColor = isDark ? 'shadow-blue-900/40' : 'shadow-blue-500/20';

  // ── Mock data — replace these with real API responses ──────────────────
  // eslint-disable-next-line
  const [orgData, setOrgData] = useState({
    name: org?.org_name || 'TechCorp Pvt Ltd',
    email: org?.org_email || 'admin@techcorp.com',
    phone: org?.phone || '+91 98765 43210',
    gst: org?.gst || 'GST27AABCT1234A1Z5',
    address: org?.address || '4th Floor, DLF Tower, Sector 25, Gurugram, Haryana - 122001',
    memberSince: org?.createdAt || 'Jan 2024',
    plan: {
      name: 'Pro',
      price: 2999,
      cycle: 'Monthly',
      status: 'Active',
      startDate: 'Jan 1, 2025',
      nextRenewal: 'Jun 1, 2025',
      features: ['10,000 API Calls/mo', '10 GB Storage', '10 Users', 'Priority Support', 'Analytics Dashboard'],
    },
    wallet: {
      balance: org?.wallet?.balance ?? 9160,
      totalAdded: 18500,
      usedThisMonth: 3240,
      remaining: 9160,
    },
    usage: {
      apiCalls: { used: 7200, total: 10000 },
      storage: { used: 4.5, total: 10, unit: ' GB' },
      users: { used: 8, total: 10 },
    },
    products: [
      { id: 1, name: 'SaaS Pro Suite', purchaseDate: 'Jan 1, 2025', amount: 2999, status: 'Active', nextBill: 'Jun 1, 2025' },
      { id: 2, name: 'Extra Storage Add-on (5GB)', purchaseDate: 'Mar 10, 2025', amount: 299, status: 'Active', nextBill: 'Jun 10, 2025' },
      { id: 3, name: 'Advanced Analytics', purchaseDate: 'Feb 5, 2025', amount: 499, status: 'Active', nextBill: 'Jun 5, 2025' },
    ],
    invoices: [
      { id: 'INV-2025-005', date: 'May 1, 2025', amount: 3797, status: 'Paid', dueDate: 'May 7, 2025' },
      { id: 'INV-2025-004', date: 'Apr 1, 2025', amount: 3797, status: 'Paid', dueDate: 'Apr 7, 2025' },
      { id: 'INV-2025-003', date: 'Mar 1, 2025', amount: 3797, status: 'Paid', dueDate: 'Mar 7, 2025' },
      { id: 'INV-2025-002', date: 'Feb 1, 2025', amount: 3797, status: 'Paid', dueDate: 'Feb 7, 2025' },
    ],
  });

  const [managers, setManagers] = useState([
    { id: 1, name: 'Rahul Sharma', email: 'rahul@techcorp.com', role: 'Operations Manager', hasBillingAccess: true },
    { id: 2, name: 'Priya Singh', email: 'priya@techcorp.com', role: 'Finance Manager', hasBillingAccess: true },
    { id: 3, name: 'Arjun Mehta', email: 'arjun@techcorp.com', role: 'IT Manager', hasBillingAccess: false },
    { id: 4, name: 'Kavita Reddy', email: 'kavita@techcorp.com', role: 'HR Manager', hasBillingAccess: false },
  ]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const handlePay = async (amount) => {
    const order = await dispatch(Create_Order({ amount }));
    const options = {
      key: 'YOUR_RAZORPAY_KEY_ID',
      amount: order.amount,
      currency: order.currency,
      name: orgData.name,
      description: 'Wallet Recharge',
      order_id: order.id,
      handler: async (response) => {
        const result = await dispatch(Verify_Payment({
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        }));
        if (result?.data?.success) {
          alert('Payment Successful ✅');
        } else {
          alert('Payment Failed ❌');
        }
      },
      prefill: { name: orgData.name, email: orgData.email },
      theme: { color: '#1d4ed8' },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
    setShowRecharge(false);
  };

  const handleSaveManagers = (selectedIds) => {
    setManagers(prev =>
      prev.map(m => ({ ...m, hasBillingAccess: selectedIds.includes(m.id) }))
    );
  };

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'products', label: 'Products & Bills' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'access', label: 'Manager Access' },
  ];

  if (loading) {
    return (
      <div className={`min-h-screen ${bgPrimary} flex items-center justify-center transition-colors duration-300`}>
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-3 border-2 border-blue-500 rounded-full border-t-transparent animate-spin" />
          <p className={`text-sm ${isDark ? 'text-blue-400/60' : 'text-gray-500'}`}>Loading billing info...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgPrimary} text-white font-sans p-4 md:p-6 transition-colors duration-300`}>
      <div className="max-w-6xl pt-16 mx-auto space-y-6">

        {/* ── Org Header ─────────────────────────────────────────────────── */}
        <div className={`rounded-2xl border ${borderColor} ${isDark ? 'bg-gradient-to-r from-blue-950/60 to-[#0B0F1C]' : 'bg-gradient-to-r from-blue-50/80 to-white'} p-6 transition-colors duration-300`}>
          <div className="flex flex-col gap-5 md:flex-row md:items-center">
            <div className="flex items-center flex-1 gap-4">
              <div className={`flex items-center justify-center flex-shrink-0 text-xl font-bold border shadow-lg w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-900 ${isDark ? 'border-blue-500/20 shadow-blue-900/40' : 'border-blue-400 shadow-blue-200'} transition-colors duration-300`}>
                {orgData.name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h1 className={`text-xl font-bold ${textPrimary}`}>{orgData.name}</h1>
                <p className={`text-sm ${textSecondary}`}>{orgData.email}</p>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  <span className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>📞 {orgData.phone}</span>
                  <span className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>•</span>
                  <span className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>Member since {orgData.memberSince}</span>
                </div>
              </div>
            </div>

            {/* Wallet + Recharge */}
            <div className={`${isDark ? 'bg-black/50 border-blue-700/40' : 'bg-white/80 border-gray-300'} border rounded-xl p-4 min-w-[200px] transition-colors duration-300`}>
              <p className={`mb-1 text-xs font-medium tracking-wider uppercase ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Wallet Balance</p>
              <p className={`text-3xl font-bold ${textPrimary}`}>
                ₹{orgData.wallet.balance.toLocaleString('en-IN')}
                <span className={`ml-1 text-sm font-normal ${isDark ? 'text-blue-400/60' : 'text-gray-500'}`}>INR</span>
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                <span className="text-xs text-green-400/70">Active</span>
              </div>
              <button
                onClick={() => setShowRecharge(true)}
                className="w-full py-2 mt-3 text-xs font-semibold text-white transition-colors bg-blue-700 rounded-lg hover:bg-blue-600"
              >
                + Recharge Wallet
              </button>
            </div>
          </div>

          {/* Org Details Row */}
          <div className={`grid grid-cols-1 gap-3 pt-4 mt-4 border-t ${isDark ? 'border-blue-800/20' : 'border-gray-200'}`}>
            <div>
              <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'} mb-0.5`}>GST Number</p>
              <p className={`font-mono text-sm ${isDark ? 'text-blue-200' : 'text-gray-700'}`}>{orgData.gst}</p>
            </div>
            <div>
              <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'} mb-0.5`}>Billing Address</p>
              <p className={`text-sm leading-snug ${isDark ? 'text-blue-200' : 'text-gray-700'}`}>{orgData.address}</p>
            </div>
            <div>
              <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'} mb-0.5`}>Current Plan</p>
              <div className="flex items-center gap-2">
                <Badge status={orgData.plan.name.toLowerCase()} />
                <Badge status={orgData.plan.status.toLowerCase()} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Grid ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Total Added" value={`₹${orgData.wallet.totalAdded.toLocaleString('en-IN')}`} sub="All time recharges" color="blue" icon="💰" />
          <StatCard label="This Month" value={`₹${orgData.wallet.usedThisMonth.toLocaleString('en-IN')}`} sub="May 2025" color="amber" icon="📅" />
          <StatCard label="Remaining" value={`₹${orgData.wallet.remaining.toLocaleString('en-IN')}`} sub="Available balance" color="green" icon="✅" />
          <StatCard label="Next Renewal" value={orgData.plan.nextRenewal} sub={`₹${orgData.plan.price}/mo`} color="blue" icon="🔄" />
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div className={`flex gap-1 p-1 border ${bgTabContainer} ${borderColor} rounded-xl w-fit transition-colors duration-300`}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === t.id
                  ? `${bgTabActive} text-white shadow-lg ${shadowColor}`
                  : `${bgTabInactive}`
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Overview ───────────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Plan Details */}
            <div className={`p-5 border rounded-xl ${borderColor} ${bgSection} transition-colors duration-300`}>
              <h3 className={`flex items-center gap-2 mb-4 text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                <span>📋</span> Plan Details
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {[
                  ['Plan', orgData.plan.name],
                  ['Billing Cycle', orgData.plan.cycle],
                  ['Plan Cost', `₹${orgData.plan.price}/mo`],
                  ['Status', <Badge status={orgData.plan.status.toLowerCase()} />],
                  ['Start Date', orgData.plan.startDate],
                  ['Next Renewal', orgData.plan.nextRenewal],
                ].map(([label, val]) => (
                  <div key={label}>
                    <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'} mb-0.5`}>{label}</p>
                    <p className={`text-sm font-medium ${textPrimary}`}>{val}</p>
                  </div>
                ))}
              </div>
              <div className={`pt-3 border-t ${isDark ? 'border-blue-800/20' : 'border-gray-200'}`}>
                <p className={`mb-2 text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>Included Features</p>
                <div className="flex flex-wrap gap-1.5">
                  {orgData.plan.features.map(f => (
                    <span key={f} className={`text-xs ${isDark ? 'bg-blue-900/30 border-blue-700/30 text-blue-300' : 'bg-blue-100 border-blue-300 text-blue-700'} px-2 py-0.5 rounded-full border`}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Usage */}
            <div className={`p-5 border rounded-xl ${borderColor} ${bgSection} transition-colors duration-300`}>
              <h3 className={`flex items-center gap-2 mb-4 text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                <span>📊</span> Usage This Month
              </h3>
              <UsageBar label="API Calls" used={orgData.usage.apiCalls.used} total={orgData.usage.apiCalls.total} unit="" />
              <UsageBar label="Storage" used={orgData.usage.storage.used} total={orgData.usage.storage.total} unit=" GB" />
              <UsageBar label="Active Users" used={orgData.usage.users.used} total={orgData.usage.users.total} unit="" />
              <div className={`pt-3 mt-4 border-t ${isDark ? 'border-blue-800/20' : 'border-gray-200'}`}>
                <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>Billing resets on {orgData.plan.nextRenewal}</p>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab: Products & Bills ────────────────────────────────────────── */}
        {activeTab === 'products' && (
          <div className={`p-5 border rounded-xl ${borderColor} ${bgSection} transition-colors duration-300`}>
            <h3 className={`flex items-center gap-2 mb-4 text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              <span>📦</span> Purchased Products
            </h3>
            <div className="space-y-3">
              {orgData.products.map(p => (
                <div key={p.id} className={`flex flex-col justify-between gap-3 p-4 border sm:flex-row sm:items-center rounded-xl ${borderItemColor} ${bgItem} transition-colors duration-300`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center text-base border rounded-lg w-9 h-9 ${isDark ? 'bg-blue-900/40 border-blue-700/30' : 'bg-blue-100 border-blue-300'}`}>
                      📦
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${textPrimary}`}>{p.name}</p>
                      <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>Purchased: {p.purchaseDate}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                    <div className="text-right">
                      <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>Monthly Bill</p>
                      <p className={`text-sm font-bold ${textPrimary}`}>₹{p.amount.toLocaleString('en-IN')}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>Next Bill</p>
                      <p className={`text-sm ${isDark ? 'text-blue-200' : 'text-gray-700'}`}>{p.nextBill}</p>
                    </div>
                    <Badge status={p.status.toLowerCase()} />
                  </div>
                </div>
              ))}
            </div>
            <div className={`flex items-center justify-between pt-4 mt-4 border-t ${isDark ? 'border-blue-800/20' : 'border-gray-200'}`}>
              <p className={`text-sm ${isDark ? 'text-blue-400/60' : 'text-gray-500'}`}>Total monthly commitment</p>
              <p className={`text-lg font-bold ${textPrimary}`}>
                ₹{orgData.products.reduce((s, p) => s + p.amount, 0).toLocaleString('en-IN')}/mo
              </p>
            </div>
          </div>
        )}

        {/* ── Tab: Invoices ────────────────────────────────────────────────── */}
        {activeTab === 'invoices' && (
          <div className={`p-5 border rounded-xl ${borderColor} ${bgSection} transition-colors duration-300`}>
            <h3 className={`flex items-center gap-2 mb-4 text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              <span>🧾</span> Invoices
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-blue-800/30' : 'border-gray-200'}`}>
                    {['Invoice ID', 'Date', 'Due Date', 'Amount', 'Status', ''].map(h => (
                      <th key={h} className={`pb-3 pr-4 text-xs font-medium tracking-wider text-left uppercase ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDark ? 'divide-blue-900/30' : 'divide-gray-200'}`}>
                  {orgData.invoices.map(inv => (
                    <tr key={inv.id} className={`transition-colors ${bgTableHover}`}>
                      <td className={`py-3 pr-4 font-mono text-xs ${isDark ? 'text-blue-300' : 'text-gray-600'}`}>{inv.id}</td>
                      <td className={`py-3 pr-4 ${isDark ? 'text-blue-200' : 'text-gray-700'}`}>{inv.date}</td>
                      <td className={`py-3 pr-4 ${isDark ? 'text-blue-200' : 'text-gray-700'}`}>{inv.dueDate}</td>
                      <td className={`py-3 pr-4 font-semibold ${textPrimary}`}>₹{inv.amount.toLocaleString('en-IN')}</td>
                      <td className="py-3 pr-4"><Badge status={inv.status.toLowerCase()} /></td>
                      <td className="py-3">
                        <button className={`px-2 py-1 text-xs transition-colors border rounded-lg ${isDark ? 'text-blue-400 border-blue-800/40 hover:text-blue-200 hover:bg-blue-900/20' : 'text-blue-600 border-gray-300 hover:text-blue-800 hover:bg-gray-100'}`}>
                          Download PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Tab: Manager Access ──────────────────────────────────────────── */}
        {activeTab === 'access' && (
          <div className={`p-5 border rounded-xl ${borderColor} ${bgSection} transition-colors duration-300`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`flex items-center gap-2 text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                <span>👥</span> Manager Billing Access
              </h3>
              <button
                onClick={() => setShowManagerModal(true)}
                className="text-xs px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white rounded-lg transition-colors font-medium"
              >
                Manage Access
              </button>
            </div>
            <p className={`mb-4 text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>
              Managers with access can view billing, plans, invoices, and transaction history.
            </p>
            <div className="space-y-2">
              {managers.map(m => (
                <div key={m.id} className={`flex items-center justify-between p-3 border rounded-xl ${borderItemColor} ${bgItem} transition-colors duration-300`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center text-xs font-bold rounded-full w-9 h-9 ${isDark ? 'text-blue-200 bg-blue-800/40' : 'text-blue-700 bg-blue-200'}`}>
                      {m.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${textPrimary}`}>{m.name}</p>
                      <p className={`text-xs ${isDark ? 'text-blue-400/50' : 'text-gray-500'}`}>{m.role} · {m.email}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                    m.hasBillingAccess
                      ? isDark ? 'bg-green-900/30 text-green-300 border-green-700/30' : 'bg-green-100 text-green-700 border-green-300'
                      : isDark ? 'bg-gray-800 text-gray-500 border-gray-700' : 'bg-gray-200 text-gray-500 border-gray-200'
                  }`}>
                    {m.hasBillingAccess ? '✓ Has Access' : 'No Access'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ── Modals ──────────────────────────────────────────────────────────── */}
      {showRecharge && (
        <RechargeModal onClose={() => setShowRecharge(false)} onPay={handlePay} />
      )}
      {showManagerModal && (
        <ManagerAccessModal
          managers={managers}
          onClose={() => setShowManagerModal(false)}
          onSave={handleSaveManagers}
        />
      )}
    </div>
  );
};

export default OrgBillingDashboard;
